import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Box, IconButton, Tooltip as MuiTooltip } from "@mui/material";
import { HelpOutline as HelpIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { getAggregateDataSummary } from "../../store/modules/analytics/analyticsActions";
import { AggregationTypesEnum } from "../../types/types";
import { formatCurrencyForStats, getShortMonthName } from "../../utils/common";
import { useDataExplanation } from "../../hooks/useDataExplanation";
import { DataExplanationDialog } from "../Dialogs/DataExplanationDialog";
import { chartYearLabel, useChartYears } from "../../hooks/usePipeline";

interface ChartDataPoint {
  year?: number;
  month?: number;
  quarter?: number;
  totalCount: number;
  totalAmount: number;
  average: number;
  displayPeriod?: string;
  periodKey?: string;
}

interface Props {
  aggregationType: AggregationTypesEnum;
  mode: "count" | "amount" | "average";
  selectedYearIds?: number[];
  timePeriod: "quarter" | "month" | "year";
  selectedTypeIds: number[];
  coAgentIds?: number[];
}

const AggregateTrendChart: React.FC<Props> = ({
  aggregationType,
  mode,
  selectedYearIds,
  timePeriod,
  selectedTypeIds,
  coAgentIds,
}) => {
  const isSingleYearSelected = selectedYearIds?.length === 1;

  const {
    explainData,
    explanation,
    isLoading,
    isOpen,
    closeExplanation,
    dailyLimitInfo,
  } = useDataExplanation({
    onError: (error) => console.error("Data explanation error:", error),
  });

  const { data } = useQuery({
    queryKey: [
      "aggregateDataSummary",
      selectedYearIds,
      selectedTypeIds,
      aggregationType,
      coAgentIds,
    ],
    queryFn: async (): Promise<ChartDataPoint[]> => {
      const result = await getAggregateDataSummary(
        selectedYearIds ?? [],
        selectedTypeIds,
        aggregationType,
        coAgentIds,
      );
      return (
        result?.map((item) => ({
          year: item.yearId,
          month: item.month,
          quarter: item.quarter,
          totalCount: item.totalCount || 0,
          totalAmount: item.totalAmount || 0,
          average: 0,
        })) || []
      );
    },
    refetchOnWindowFocus: false,
  });

  const years = useChartYears(selectedYearIds);

  // Generate all periods for the selected years and time period
  const allPeriods = useMemo(() => {
    if (isSingleYearSelected) {
      const periodCount = timePeriod === "month" ? 12 : 4;
      return Array.from({ length: periodCount }, (_, i) => ({
        yearId: selectedYearIds![0],
        [timePeriod]: i + 1,
        periodKey: `${selectedYearIds![0]}-${timePeriod}-${i + 1}`,
      }));
    } else {
      // For multiple years, generate all combinations of years and periods
      const periods: any[] = [];
      const relevantYears = selectedYearIds?.length
        ? years.filter((y) => selectedYearIds.includes(y.id!))
        : years;

      // Sort years by year value (ascending) to ensure consistent chronological order
      // Create a copy of the array before sorting to avoid mutating the original
      const sortedYears = [...relevantYears].sort(
        (a, b) => (a.year || 0) - (b.year || 0),
      );

      sortedYears.forEach((year) => {
        if (timePeriod === "year") {
          periods.push({
            yearId: year.id,
            year: year.year,
            periodKey: `${year.id}-year`,
          });
        } else {
          const periodCount = timePeriod === "month" ? 12 : 4;
          for (let i = 1; i <= periodCount; i++) {
            periods.push({
              yearId: year.id,
              [timePeriod]: i,
              periodKey: `${year.id}-${timePeriod}-${i}`,
            });
          }
        }
      });
      return periods;
    }
  }, [selectedYearIds, timePeriod, years, isSingleYearSelected]);

  const mergedData: ChartDataPoint[] = useMemo(() => {
    return allPeriods
      .map((period: any) => {
        // Find all matching data points for this period
        let matchingDataPoints: ChartDataPoint[] = [];

        if (timePeriod === "year") {
          matchingDataPoints =
            data?.filter((d: ChartDataPoint) => d.year === period.yearId) || [];
        } else {
          matchingDataPoints =
            data?.filter(
              (d: ChartDataPoint) =>
                d.year === period.yearId &&
                d[timePeriod] === period[timePeriod],
            ) || [];
        }

        // Aggregate all matching data points
        const totalCount = matchingDataPoints.reduce(
          (sum, item) => sum + (item.totalCount || 0),
          0,
        );
        const totalAmount = matchingDataPoints.reduce(
          (sum, item) => sum + (item.totalAmount || 0),
          0,
        );

        // Get the year object for display purposes
        const yearObj = years.find((y) => y.id === period.yearId);
        const yearValue = chartYearLabel(yearObj?.year) ?? period.yearId;

        // Generate display period for multi-year scenarios
        let displayPeriod: string;
        if (timePeriod === "year") {
          displayPeriod = `${yearValue}`;
        } else if (isSingleYearSelected) {
          displayPeriod =
            timePeriod === "month"
              ? getShortMonthName(period[timePeriod])
              : `Q${period[timePeriod]}`;
        } else {
          displayPeriod =
            timePeriod === "month"
              ? `${yearValue} ${getShortMonthName(period[timePeriod])}`
              : `${yearValue} Q${period[timePeriod]}`;
        }

        const average = totalCount > 0 ? totalAmount / totalCount : 0;

        return {
          year: period.yearId,
          month: period.month,
          quarter: period.quarter,
          totalCount,
          totalAmount,
          average: mode === "count" ? totalCount : average,
          displayPeriod,
          periodKey: period.periodKey,
        };
      })
      .sort((a: any, b: any) => {
        // Sort by year first, then by period
        const yearA = years.find((y) => y.id === a.year)?.year || a.year;
        const yearB = years.find((y) => y.id === b.year)?.year || b.year;

        if (yearA !== yearB) {
          return yearA - yearB;
        }

        if (timePeriod === "year") return 0;

        const periodA = timePeriod === "month" ? a.month : a.quarter;
        const periodB = timePeriod === "month" ? b.month : b.quarter;

        return (periodA || 0) - (periodB || 0);
      });
  }, [allPeriods, data, years, timePeriod, isSingleYearSelected, mode]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const dataItem: ChartDataPoint = payload[0].payload;
    const value: number = payload[0].value;
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <p style={{ fontWeight: "bold", marginBottom: "5px" }}>
          {dataItem.displayPeriod}
        </p>
        <p>
          {mode === "count"
            ? aggregationType === AggregationTypesEnum.Total
              ? `Units: ${dataItem.totalCount.toFixed(0)}`
              : `Avg Units: ${dataItem.average.toFixed(2)}`
            : `Volume: ${formatCurrencyForStats(value)}`}
        </p>
      </div>
    );
  };

  const yAxisLabel =
    aggregationType === AggregationTypesEnum.Total
      ? mode === "count"
        ? "Total Units"
        : "Total Volume"
      : mode === "count"
        ? "Average Units"
        : "Average Price";

  const handleExplainData = () => {
    const aggregationTypeText =
      aggregationType === AggregationTypesEnum.Total ? "Total" : "Average";
    const selectedYearText =
      (selectedYearIds ?? []).length > 0
        ? selectedYearIds!
            .map((id) => chartYearLabel(years.find((y) => y.id === id)?.year))
            .join(", ")
        : "all years";

    explainData(
      mergedData,
      "aggregate",
      mode,
      timePeriod,
      selectedYearText,
      undefined,
      undefined,
      undefined,
      aggregationTypeText,
    );
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          mt: 2,
        }}
      >
        {/* Header row with title and AI insights button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            px: 1,
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1rem" }}>
            {/* {aggregationType === AggregationTypesEnum.Total
              ? "Total"
              : "Average"}{" "}
            {mode.charAt(0).toUpperCase() + mode.slice(1)} Trend */}
          </Box>
          <MuiTooltip title="Get AI insights about this trend data">
            <IconButton
              size="small"
              onClick={handleExplainData}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>

        {/* Chart container */}
        <Box
          sx={{
            width: "100%",
            height: 400,
            padding: 1,
            paddingRight: 2,
            overflow: "visible",
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={mergedData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: isSingleYearSelected ? 50 : 80,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="displayPeriod"
                axisLine={false}
                tick={{ fontSize: 14, fontFamily: "Segoe UI" }}
                tickLine={false}
                type="category"
                interval={0}
                angle={isSingleYearSelected ? 0 : -45}
                textAnchor={isSingleYearSelected ? "middle" : "end"}
                height={isSingleYearSelected ? 50 : 80}
                label={{
                  value: isSingleYearSelected
                    ? timePeriod === "month"
                      ? "Month"
                      : "Quarter"
                    : timePeriod === "year"
                      ? "Year"
                      : `${
                          timePeriod.charAt(0).toUpperCase() +
                          timePeriod.slice(1)
                        }s`,
                  position: "insideBottomRight",
                  offset: -5,
                }}
              />
              <YAxis
                fontSize={13}
                domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                tickFormatter={(value) =>
                  mode === "count"
                    ? `${value.toFixed(0)}`
                    : formatCurrencyForStats(value)
                }
                label={{
                  position: "insideLeft",
                  offset: 0,
                }}
                width={70}
                allowDataOverflow={true}
                allowDecimals={false}
                padding={{ top: 20 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" align="left" />
              <Line
                type="monotone"
                dataKey={
                  aggregationType === AggregationTypesEnum.Average
                    ? "average"
                    : mode === "count"
                      ? "totalCount"
                      : "totalAmount"
                }
                stroke="#8884d8"
                strokeWidth={3}
                activeDot={{ r: 8 }}
                name={yAxisLabel}
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <DataExplanationDialog
        open={isOpen}
        onClose={closeExplanation}
        explanation={explanation}
        isLoading={isLoading}
        title={`${
          aggregationType === AggregationTypesEnum.Total ? "Total" : "Average"
        } ${mode.charAt(0).toUpperCase() + mode.slice(1)} Trend Analysis`}
        dailyLimitInfo={dailyLimitInfo}
      />
    </>
  );
};

export default AggregateTrendChart;
